import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Avatar, Box, Chip, Stack, Typography,
} from '@mui/material';
import { Check, ExpandMore } from '@mui/icons-material';
import ButtonOne from '../../../common/normal-button';
import { ColorsConstants } from '../../../cssUtils/constants';

const accordionSx = {
  border: `1px solid ${ColorsConstants.primaryGray10}`,
  borderRadius: '8px !important',
  boxShadow: 'none',
  mb: 1.5,
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    border: `1.5px solid ${ColorsConstants.primaryBlue90}`,
    boxShadow: '0 4px 16px rgba(8,36,92,0.08)',
  },
};

// One step of the create flow. Errors show only after Done was pressed (`showErrors`), never on open.
export const Section = ({ num, title, summary, open, onToggle, isDone, errors = [], showErrors, onDone, children }) => {
  const visibleErrors = showErrors ? errors : [];
  return (
    <Accordion expanded={open} onChange={onToggle} sx={accordionSx}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, mr: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 28, height: 28, fontSize: 13, fontWeight: 700, flexShrink: 0,
              backgroundColor: isDone && !errors.length ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20,
              color: isDone && !errors.length ? 'white' : ColorsConstants.primaryGray50,
            }}
          >
            {isDone && !errors.length ? <Check sx={{ fontSize: 14 }} /> : num}
          </Avatar>
          <Typography variant="body2" fontWeight={600} color={ColorsConstants.primaryGray60} sx={{ flexShrink: 0 }}>
            {title}
          </Typography>
          {showErrors && errors.length > 0 && (
            <Chip size="small" label={`${errors.length} to fix`} sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: '#FEE2E2', color: '#991B1B' }} />
          )}
          {!open && summary && (
            <Typography variant="caption" color={ColorsConstants.primaryGray50} sx={{ ml: 'auto', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
        {children}
        {visibleErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {visibleErrors.map((e, i) => <Box key={i}>{e}</Box>)}
          </Alert>
        )}
        {onDone && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
            <ButtonOne title="Done" onClick={onDone} />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export const FieldLabel = ({ children, hint }) => (
  <Typography variant="caption" fontWeight={700} color={ColorsConstants.primaryGray60} sx={{ mb: 0.5, display: 'block' }}>
    {children}
    {hint && <Box component="span" sx={{ fontWeight: 400, color: ColorsConstants.primaryGray50, ml: 0.5 }}>{hint}</Box>}
  </Typography>
);

export const Note = ({ children, sx }) => (
  <Typography variant="caption" color={ColorsConstants.primaryGray50} sx={{ display: 'block', ...sx }}>{children}</Typography>
);

// Selectable bordered card used for radio-style choices.
export const ChoiceCard = ({ selected, onClick, title, desc, disabled }) => (
  <Box
    component="button"
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    sx={{
      flex: 1, textAlign: 'left', font: 'inherit', p: 2, borderRadius: 2,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      border: `2px solid ${selected ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray20}`,
      backgroundColor: selected ? ColorsConstants.primaryBlue60 : 'white',
    }}
  >
    <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray60}>{title}</Typography>
    {desc && <Typography variant="caption" color={ColorsConstants.primaryGray50}>{desc}</Typography>}
  </Box>
);

export const currencySymbol = (currency) => ({ INR: '₹', USD: '$', AED: 'AED ', GBP: '£', EUR: '€', SGD: 'S$', SAR: 'SAR ' }[currency] ?? `${currency || ''} `);
